import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ProfileView = () => {
  const { /* currentUser */ } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await api.get('/me');
        setProfileData(response.data);
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading profile...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <div className="text-center mt-3">
          <Button onClick={() => window.location.reload()} variant="primary">
            Try Again
          </Button>
        </div>
      </Container>
    );
  }

  const { user, profile, email, counters } = profileData || {};

  return (
    <Container className="py-4">
      <Row>
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center p-4">
              <div className="mb-3">
                {user?.avatars?.medium ? (
                  <img
                    src={user.avatars.medium}
                    alt={user.username}
                    className="avatar-large mx-auto d-block"
                  />
                ) : (
                  <div className="avatar-large mx-auto d-flex align-items-center justify-content-center bg-light rounded-circle">
                    <i className="bi bi-person-fill fs-1 text-secondary"></i>
                  </div>
                )}
              </div>

              <h3 className="fw-bold mb-1">{user?.username}</h3>
              <p className="text-muted mb-3">
                {user?.age} • {user?.city}, {user?.region}
              </p>

              {profile?.status === 'Pending' && (
                <Alert variant="warning" className="mb-3">
                  <small>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {profile.denial_reason || 'Your profile is pending approval.'}
                  </small>
                </Alert>
              )}

              <div className="d-grid gap-2">
                <Button as={Link} to="/profile/edit" className="btn-profile">
                  Edit Profile
                </Button>
                <Button as={Link} to="/photos" className="btn-profile">
                  Manage Photos
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mt-4">
            <Card.Body>
              <h5 className="fw-bold mb-3">Account Information</h5>

              <div className="mb-3">
                <small className="text-muted d-block">Email</small>
                <div className="d-flex align-items-center">
                  <span>{email?.email}</span>
                  {email?.confirmed ? (
                    <Badge bg="success" className="ms-2">Verified</Badge>
                  ) : (
                    <Badge bg="warning" text="dark" className="ms-2">Unverified</Badge>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">Member Since</small>
                <span>{new Date(profileData?.date_joined).toLocaleDateString()}</span>
              </div>

              <div>
                <small className="text-muted d-block">Account Status</small>
                <span>{user?.status}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">Profile Details</h4>

              <Row className="mb-4">
                <Col sm={6} className="mb-3">
                  <small className="text-muted d-block">Gender</small>
                  <span>{profile?.sex}</span>
                </Col>
                <Col sm={6} className="mb-3">
                  <small className="text-muted d-block">Orientation</small>
                  <span>{profile?.orientation}</span>
                </Col>
                <Col sm={6} className="mb-3">
                  <small className="text-muted d-block">Looking To</small>
                  <span>{profile?.looking_to}</span>
                </Col>
                <Col sm={6} className="mb-3">
                  <small className="text-muted d-block">Location</small>
                  <span>{user?.city}, {user?.region}, {user?.country}</span>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            <Col md={6} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-envelope-fill fs-4 text-primary me-2"></i>
                    <h5 className="fw-bold mb-0">Messages</h5>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Messages</span>
                    <span className="fw-bold">{counters?.messages?.messages || 0}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span>Offers</span>
                    <span className="fw-bold">{counters?.messages?.offers || 0}</span>
                  </div>

                  <div className="d-grid">
                    <Button as={Link} to="/messages" className="btn-profile" size="sm">
                      View Messages
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-graph-up fs-4 text-primary me-2"></i>
                    <h5 className="fw-bold mb-0">Activity</h5>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Profile Views</span>
                    <span className="fw-bold">{counters?.activity?.views || 0}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span>Favorites</span>
                    <span className="fw-bold">{counters?.activity?.favorites || 0}</span>
                  </div>

                  <div className="d-grid gap-2">
                    <Button as={Link} to="/favorite-me" className="btn-profile" size="sm">
                      Who Favorited Me
                    </Button>
                    <Button as={Link} to="/favorited-users" className="btn-profile" size="sm">
                      <i className="bi bi-heart me-2"></i>
                      My Favorites
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md={12} className="mb-4">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-shield-slash fs-4 text-primary me-2"></i>
                      <h5 className="fw-bold mb-0">Privacy & Security</h5>
                    </div>
                    <Button as={Link} to="/blocked-users" className="btn-profile" size="sm">
                      <i className="bi bi-person-slash me-2"></i>
                      Blocked Users
                    </Button>
                  </div>
                  <p className="text-muted mb-0">
                    Manage your blocked users and privacy settings
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-bold mb-0">Credits Balance</h5>
                <Badge bg="primary" className="px-3 py-2">
                  <i className="bi bi-coin me-1"></i>
                  {profileData?.balance?.credits || 0} Credits
                </Badge>
              </div>

              <p className="text-muted mb-3">
                Credits can be used to unlock conversations, boost your profile, and more.
              </p>

              <div className="d-grid">
                <Button as={Link} to="/credits" className="btn-profile">
                  Buy Credits
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfileView;
